import React from 'react';
import {
  Container,
  Grid,
  Button,
  Typography,
  Card,
  Box,
  Stack,
  Chip,
  TextField as MuiTextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Page from 'src/components/Page';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { transactionService } from 'src/services/transaction.service';
import { ChargeMode } from 'src/types/transaction.types';
import ChargeModeField from './ChargeModeField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Enter a name').min(2, 'Name must be at least 2 characters'),
  description: Yup.string().trim().required('Add a short description').max(300, 'Description cannot exceed 300 characters'),
  type: Yup.string().oneOf(['credit', 'debit']).required('Choose money in or money out'),
  priceType: Yup.string().oneOf(['fixed', 'variable']).required('Choose a set price or any amount'),
  amount: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .when('priceType', {
      is: 'fixed',
      then: (schema) => schema.required('Enter the set price').min(0.01, 'Amount must be greater than $0'),
      otherwise: (schema) => schema.nullable().optional(),
    }),
});

export default function AddTransactionType(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getBaseRoute } = useAuth();
  const backRoute = getBaseRoute('/manage-transaction-types');

  useHeaderBreadcrumbs({
    backUrl: backRoute,
    backLabel: 'Transaction Catalog',
    breadcrumbs: [
      { label: 'S.T.R.E.E.T Cents', path: backRoute },
      { label: 'Create Transaction Type' },
    ],
  });

  const createMutation = useMutation({
    mutationFn: (values: {
      name: string;
      description: string;
      type: 'debit' | 'credit';
      points: number;
      priceType: 'fixed' | 'variable';
      chargeMode: ChargeMode;
    }) => transactionService.createTransactionType(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactionTypes'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-reasons'] });
      toast.success('Transaction type created successfully');
      navigate(backRoute);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create transaction type');
    },
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      type: 'credit' as 'credit' | 'debit',
      priceType: 'variable' as 'fixed' | 'variable',
      chargeMode: 'auto' as ChargeMode,
      amount: '',
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      await createMutation.mutateAsync({
        name: values.name.trim(),
        description: values.description.trim(),
        type: values.type,
        points: values.priceType === 'fixed' ? Number(values.amount) : 0,
        priceType: values.priceType,
        chargeMode: values.chargeMode,
      });
    },
  });

  const isCredit = formik.values.type === 'credit';
  const isFixed = formik.values.priceType === 'fixed';
  const previewAmount = isFixed && formik.values.amount ? Number(formik.values.amount) : 25;

  return (
    <Page title="Create Transaction Type | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Header Ribbon */}
        <Box sx={{ mb: 2.5 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<Icon icon="solar:arrow-left-linear" width={16} />}
            onClick={() => navigate(backRoute)}
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.8125rem',
              p: 0,
              mb: 1,
              '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
            }}
          >
            Back to Transaction Types Catalog
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', fontSize: '1.375rem' }}>
            Create Transaction Type
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
            Create something to pay students for or charge them for, like a job, a fine or rent.
          </Typography>
        </Box>

        {/* Form & Live Preview 2-Column Grid */}
        <Grid container spacing={2.5}>
          {/* Left Column: Form Fields */}
          <Grid item xs={12} md={7.5}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={2.5}>
                  {/* Name Field */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.75, display: 'block' }}>
                      Transaction Type Name *
                    </Typography>
                    <MuiTextField
                      fullWidth
                      size="small"
                      name="name"
                      placeholder="e.g. Monthly Rent, Materials Manager, Bathroom Fee"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={Boolean(formik.touched.name && formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      InputProps={{
                        sx: { borderRadius: 1.5, fontSize: '0.875rem' },
                      }}
                    />
                  </Box>

                  {/* Flow Category Selector */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.75, display: 'block' }}>
                      Money in or money out? *
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Paper
                          onClick={() => formik.setFieldValue('type', 'credit')}
                          elevation={0}
                          sx={{
                            p: 1.75,
                            borderRadius: 1.75,
                            border: '1.5px solid',
                            borderColor: isCredit ? '#059669' : 'divider',
                            bgcolor: isCredit ? 'rgba(16, 185, 129, 0.08)' : 'background.paper',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            '&:hover': { borderColor: '#059669', bgcolor: 'rgba(16, 185, 129, 0.04)' },
                          }}
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 1.5,
                              bgcolor: 'rgba(16, 185, 129, 0.15)',
                              color: '#059669',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Icon icon="solar:card-recive-bold-duotone" width={22} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isCredit ? '#059669' : 'text.primary', fontSize: '0.875rem' }}>
                              Money in (+)
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              Pay, jobs and rewards
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Paper
                          onClick={() => formik.setFieldValue('type', 'debit')}
                          elevation={0}
                          sx={{
                            p: 1.75,
                            borderRadius: 1.75,
                            border: '1.5px solid',
                            borderColor: !isCredit ? '#DC2626' : 'divider',
                            bgcolor: !isCredit ? 'rgba(239, 68, 68, 0.08)' : 'background.paper',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            '&:hover': { borderColor: '#DC2626', bgcolor: 'rgba(239, 68, 68, 0.04)' },
                          }}
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 1.5,
                              bgcolor: 'rgba(239, 68, 68, 0.15)',
                              color: '#DC2626',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Icon icon="solar:card-send-bold-duotone" width={22} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: !isCredit ? '#DC2626' : 'text.primary', fontSize: '0.875rem' }}>
                              Money out (−)
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              Rent, bills, fines and store purchases
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Pricing Model & Default Amount */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.75, display: 'block' }}>
                      Price *
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        name="priceType"
                        value={formik.values.priceType}
                        onChange={formik.handleChange}
                      >
                        <FormControlLabel
                          value="variable"
                          control={<Radio size="small" />}
                          label={<Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Any amount — the teacher types it each time</Typography>}
                        />
                        <FormControlLabel
                          value="fixed"
                          control={<Radio size="small" />}
                          label={<Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Set price — the same amount every time</Typography>}
                        />
                      </RadioGroup>
                    </FormControl>

                    {isFixed && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                          Set price ($D) *
                        </Typography>
                        <MuiTextField
                          fullWidth
                          size="small"
                          name="amount"
                          type="number"
                          placeholder="e.g. 25.00"
                          value={formik.values.amount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={Boolean(formik.touched.amount && formik.errors.amount)}
                          helperText={formik.touched.amount && formik.errors.amount}
                          InputProps={{
                            startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary', fontWeight: 600 }}>$</Typography>,
                            sx: { borderRadius: 1.5, fontSize: '0.875rem', maxWidth: 240 },
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {formik.values.type === 'debit' && (
                    <ChargeModeField
                      value={formik.values.chargeMode}
                      onChange={(mode) => formik.setFieldValue('chargeMode', mode)}
                    />
                  )}

                  {/* Description & Guidelines */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.75, display: 'block' }}>
                      Description & Guidelines *
                    </Typography>
                    <MuiTextField
                      fullWidth
                      multiline
                      rows={3}
                      name="description"
                      placeholder="When should this be used? e.g. Charged on the 1st of every month."
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={Boolean(formik.touched.description && formik.errors.description)}
                      helperText={formik.touched.description && formik.errors.description}
                      InputProps={{
                        sx: { borderRadius: 1.5, fontSize: '0.875rem' },
                      }}
                    />
                  </Box>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate(backRoute)}
                      sx={{ fontWeight: 600, borderRadius: 1.5, textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={createMutation.isPending || !formik.isValid}
                      sx={{
                        fontWeight: 600,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        px: 3,
                        boxShadow: 'none',
                        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
                      }}
                    >
                      {createMutation.isPending ? 'Creating...' : 'Create Transaction Type'}
                    </Button>
                  </Stack>
                </Stack>
              </form>
            </Card>
          </Grid>

          {/* Right Column: Live Interactive Preview */}
          <Grid item xs={12} md={4.5}>
            <Stack spacing={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                Live Catalog & Ledger Preview
              </Typography>

              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  bgcolor: 'background.paper',
                }}
              >
                {/* Header Preview */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.75,
                      bgcolor: isCredit ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                      color: isCredit ? '#059669' : '#DC2626',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      icon={isCredit ? 'solar:card-recive-bold-duotone' : 'solar:card-send-bold-duotone'}
                      width={24}
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                      {formik.values.name || 'Untitled Transaction Type'}
                    </Typography>
                    <Chip
                      icon={
                        <Icon
                          icon={isCredit ? 'solar:card-recive-bold-duotone' : 'solar:card-send-bold-duotone'}
                          width={13}
                          color={isCredit ? '#059669' : '#DC2626'}
                        />
                      }
                      label={isCredit ? 'Money in' : 'Money out'}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        mt: 0.5,
                        bgcolor: isCredit ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: isCredit ? '#059669' : '#DC2626',
                      }}
                    />
                  </Box>
                </Stack>

                {/* Amount Preview Box */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1.75,
                    bgcolor: isCredit ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.06)',
                    border: '1px solid',
                    borderColor: isCredit ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    mb: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                    {isFixed ? 'Fixed Ledger Impact' : 'Sample Ledger Impact'}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: isCredit ? '#059669' : '#DC2626',
                      mt: 0.25,
                      fontSize: '1.375rem',
                    }}
                  >
                    {isCredit ? '+' : '-'}${previewAmount.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {isFixed ? 'Standardized price applied on entry' : 'Custom value configured per transaction'}
                  </Typography>
                </Paper>

                <Divider sx={{ my: 1.5 }} />

                {/* Description Preview */}
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Description Guidelines:
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.8125rem', lineHeight: 1.5, fontWeight: 400 }}>
                  {formik.values.description || 'No description entered yet.'}
                </Typography>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
